import { Box, Button, CircularProgress } from '@mui/material'
import { useBooleanFlagValue } from '@openfeature/react-sdk'
import { OrderStatus } from '@planning/app/api'
import { usePlanningStore } from '@planning/AppProvider'
import { CreateGeneralCargoOrder } from '@planning/pages/Order/CreateGeneralCargoOrders'
import generalCargoService from '@planning/services/generalCargoService'
import { ValidateOutboundOrderFunc } from '@planning/stores/gateControl/ValidateOutboundDto'
import { useTranslate } from '@tolgee/react'
import { Header, useMinimalsTheme } from '@tom-ui/ui'
import { PATHS, useHistory } from '@tom-ui/utils'
import { observer } from 'mobx-react-lite'
import { useEffect } from 'react'
import { useParams } from 'react-router'

interface Props {
  renderDamage?: (orderId: number) => React.ReactElement
  validateOutboundRequest?: ValidateOutboundOrderFunc
}

export const GeneralCargoOrderFormPage = observer(
  ({ renderDamage, validateOutboundRequest }: Props) => {
    const { t } = useTranslate()
    const theme = useMinimalsTheme()
    const history = useHistory()
    const { id } = useParams<{ id: string }>()
    const { generalCargoViewStore, appViewStore } = usePlanningStore()

    const isAllowGeneralCargoOrderToChangeStatusFeatureEnabled = useBooleanFlagValue(
      'gc-update-order-status',
      false,
    )

    const isEdit = !!id
    const isSubmitting = appViewStore.isLoading
    const title = isEdit
      ? t('editGeneralCargoOrder', 'Edit general cargo order')
      : t('createGeneralCargoOrder', 'Create general cargo order')

    const breadcrumbs = [
      {
        label: t('generalCargoOrders', 'General cargo orders'),
        route: PATHS.generalCargo.orders.url,
      },
      { label: title },
    ]

    const onClose = () => {
      generalCargoViewStore.selectOrder()
      history.goBackHistoryOrDefault(PATHS.generalCargo.orders.url)
    }

    useEffect(() => {
      const loadOrder = async () => {
        await generalCargoViewStore.getPackages()

        if (!id) {
          generalCargoViewStore.selectOrder()
          return
        }

        if (generalCargoViewStore.generalCargoOrdersStore.items.length === 0) {
          await generalCargoViewStore.loadAllOrders()
        }

        const orderId = Number(id)
        const existing = generalCargoViewStore.generalCargoOrdersStore.items.find(
          order => order.id === orderId,
        )

        if (existing) {
          generalCargoViewStore.selectOrder(existing)
          return
        }

        const orders = await generalCargoService.getAll()
        const match = orders.find(order => order.id === orderId)

        generalCargoViewStore.selectOrder(match)
      }

      loadOrder()
    }, [generalCargoViewStore, id])

    return (
      <Box sx={{ bgcolor: theme.palette.grey[100], minHeight: '100%' }}>
        <Header
          title={title}
          breadcrumbs={breadcrumbs}
          rightRenderOption={
            <>
              <Button color='inherit' onClick={onClose} variant='text'>
                {t('cancel', 'Cancel')}
              </Button>
              {(isAllowGeneralCargoOrderToChangeStatusFeatureEnabled ||
                generalCargoViewStore.selectedOrder?.status !== OrderStatus.Fulfilled) && (
                <Button
                  type='submit'
                  form={isEdit ? 'gc-order-edit-form' : 'gc-order-form'}
                  color='primary'
                  variant='contained'
                  disabled={isSubmitting}
                  startIcon={isSubmitting ? <CircularProgress size='1rem' /> : undefined}
                >
                  {t('submit', 'Submit')}
                </Button>
              )}
            </>
          }
        />
        <Box sx={{ paddingTop: theme.customSpacing.l, paddingBottom: theme.customSpacing.l }}>
          <CreateGeneralCargoOrder
            variant='page'
            onClose={onClose}
            renderDamage={renderDamage}
            validateOutboundRequest={validateOutboundRequest}
          />
        </Box>
      </Box>
    )
  },
)
